// safetyFormLists/safetyFormReports/safetyFormListsSafetyFormReportsActions.js

import {
  getUserNearMissFormList,
  getUserSafetyAuditChecklistFormList,
} from "../../../api/SafetyFormApi";
import { safetyFormReportsInitialState } from "./safetyFormListsSafetyFormReportsState";
import { makeFiltersActions } from "../../shared/filters/filtersActions";

export const makeSafetyFormReportsActions = (set, get) => ({
  setReportsPage: ({ currentPage, pageSize }) =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          currentPage,
          pageSize,
        },
      },
    })),

  setReportsPageSize: (pageSize) =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          pageSize,
          currentPage: 1,
        },
      },
    })),

  setSelectedForm: (form) =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          selectedForm: form,
        },
      },
    })),

  setActiveFormType: (formType) => {
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          activeFormType: formType,
          currentPage: 1,
          filters: safetyFormReportsInitialState.filters,
          items: [],
          totalCount: 0,
          selectedForm: null,
          selectedNearMissIds: [],
          selectedSafetyAuditIds: [],
        },
      },
    }));
  },

  // Near Miss selection actions
  toggleSelectedNearMiss: (formId) =>
    set((s) => {
      const current = s.safetyFormLists.safetyFormReports.selectedNearMissIds || [];
      const exists = current.includes(formId);
      const next = exists
        ? current.filter((id) => id !== formId)
        : [...current, formId];
      return {
        safetyFormLists: {
          ...s.safetyFormLists,
          safetyFormReports: {
            ...s.safetyFormLists.safetyFormReports,
            selectedNearMissIds: next,
          },
        },
      };
    }),

  setSelectedNearMissIds: (formIds) =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          selectedNearMissIds: formIds,
        },
      },
    })),

  clearSelectedNearMissIds: () =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          selectedNearMissIds: [],
        },
      },
    })),

  // Safety Audit selection actions
  toggleSelectedSafetyAudit: (formId) =>
    set((s) => {
      const current = s.safetyFormLists.safetyFormReports.selectedSafetyAuditIds || [];
      const exists = current.includes(formId);
      const next = exists
        ? current.filter((id) => id !== formId)
        : [...current, formId];
      return {
        safetyFormLists: {
          ...s.safetyFormLists,
          safetyFormReports: {
            ...s.safetyFormLists.safetyFormReports,
            selectedSafetyAuditIds: next,
          },
        },
      };
    }),

  setSelectedSafetyAuditIds: (formIds) =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          selectedSafetyAuditIds: formIds,
        },
      },
    })),

  clearSelectedSafetyAuditIds: () =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          selectedSafetyAuditIds: [],
        },
      },
    })),

  fetchSafetyFormReportsPage: async (employeeId, externalFilters = null) => {
    const {
      safetyFormLists: {
        safetyFormReports: { activeFormType, currentPage, pageSize, filters },
      },
    } = get();
    const effectiveFilters = externalFilters || filters;

    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: {
          ...s.safetyFormLists.safetyFormReports,
          loading: true,
          error: null,
        },
      },
    }));

    try {
      let response;
      if (activeFormType === "near_miss") {
        response = await getUserNearMissFormList(
          employeeId,
          currentPage,
          pageSize,
          effectiveFilters,
          true
        );
      } else {
        response = await getUserSafetyAuditChecklistFormList(
          employeeId,
          currentPage,
          pageSize,
          effectiveFilters,
          true
        );
      }

      const { data, total_count } = response;
      const hasMore = currentPage * pageSize < total_count;

      set((s) => ({
        safetyFormLists: {
          ...s.safetyFormLists,
          safetyFormReports: {
            ...s.safetyFormLists.safetyFormReports,
            items: data || [],
            totalCount: total_count || 0,
            hasMore,
            loading: false,
            needsUpdate: false,
          },
        },
      }));
    } catch (err) {
      set((s) => ({
        safetyFormLists: {
          ...s.safetyFormLists,
          safetyFormReports: {
            ...s.safetyFormLists.safetyFormReports,
            error: err.message || "Failed to fetch safety form reports",
            loading: false,
          },
        },
      }));
    }
  },

  ...makeFiltersActions(set, get, "SafetyFormReports"),

  resetSafetyFormReports: () =>
    set((s) => ({
      safetyFormLists: {
        ...s.safetyFormLists,
        safetyFormReports: safetyFormReportsInitialState,
      },
    })),
});
